import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { serializeProgrammation } from '@teetsh/app/src/containers/Programmations/atoms/serializer';
import { Periode, Programmation } from '@teetsh/app/src/types/programmations';
import { PROGRAMMATION_KEY } from '@teetsh/app/src/containers/Programmations/atoms/progAtoms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { AxiosError } from 'axios';
import { useLimitReached } from '@teetsh/app/src/components/LimitReached/useLimitReached';
import { useTranslation } from 'react-i18next';

function createProgrammation(
  schoolyearId: string,
  schoolId: string,
  programmation: Programmation,
) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/programmations`;
  return request(requestURL, {
    method: 'POST',
    body: serializeProgrammation(programmation),
  });
}

function createProgrammationFromTemplate(
  schoolyearId: string,
  schoolId: string,
  template: { templateId: string; periodes: Periode[] },
) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/programmations?strapi-template-id=${template.templateId}`;
  return request(requestURL, {
    method: 'POST',
    body: {
      periodes: template.periodes,
    },
  });
}

export function useCreateProgrammation() {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['programmation']);
  const { openLimitModal } = useLimitReached();
  const { mutateAsync } = useMutation<
    Programmation,
    AxiosError,
    {
      programmation?: Programmation;
      template?: {
        periodes: Periode[];
        templateId: string;
      };
      schoolyearId?: string;
      schoolId?: string;
    }
  >({
    mutationFn: ({
      programmation,
      template,
      schoolyearId: syId,
      schoolId: schId,
    }) => {
      const schoolyearId = syId || schoolyear.id;
      const schoolId = schId || school.id;
      if (template) {
        return createProgrammationFromTemplate(
          schoolyearId,
          schoolId,
          template,
        );
      }
      return createProgrammation(
        schoolyearId,
        schoolId,
        programmation as Programmation,
      );
    },
    onMutate: params => {
      const schoolyearId = params.schoolyearId || schoolyear.id;
      const schoolId = params.schoolId || school.id;
      if (params.programmation) {
        queryClient.setQueryData<Programmation>(
          [schoolyearId, schoolId, PROGRAMMATION_KEY, params.programmation.id],
          params.programmation,
        );
      }
    },
    onError: (err, params) => {
      const schoolyearId = params.schoolyearId || schoolyear.id;
      const schoolId = params.schoolId || school.id;
      if (err?.response?.status === 403) {
        openLimitModal({
          requiredPlan: (err?.response as any)?.data?.requiredPlan,
          limit: (err?.response as any)?.data?.limit,
        });
        return;
      }
      if (params.programmation) {
        queryClient.invalidateQueries({
          queryKey: [
            schoolyearId,
            schoolId,
            PROGRAMMATION_KEY,
            params.programmation.id,
          ],
        });
      }
      notifyError({
        title: t('programmation:messages.programmation.createError'),
      });
    },
  });
  return mutateAsync;
}
